import {
  AccountTreeOutlined,
  ArrowForward,
  AutoFixHighOutlined,
  BuildOutlined,
  FactCheckOutlined,
  HubOutlined,
  LayersOutlined,
  PrecisionManufacturingOutlined,
  QueryStatsOutlined,
  ScienceOutlined,
  SensorsOutlined,
  TuneOutlined,
} from '@mui/icons-material';
import { Box, Button, Card, CardContent, CardMedia, Container, Grid, Stack, Typography } from '@mui/material';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface UseCase {
  slug: string;
  icon: React.ReactNode;
  eyebrow: string;
  title: string;
  image: string;
  imageAlt: string;
}

const useCases: UseCase[] = [
  { slug: 'mrb',          icon: <FactCheckOutlined />,              eyebrow: 'Quality & MRB',            title: 'Automate MRB Quality Escape Modeling',                     image: '/use-cases/und_damage_1.png',        imageAlt: 'Damaged part scan for MRB modeling' },
  { slug: 'blend-repair', icon: <BuildOutlined />,                  eyebrow: 'Repair & sustainment',     title: 'Accelerate Design of Repairs with FEMORPH Blender',        image: '/use-cases/blender_pbs_3.png',       imageAlt: 'FEMORPH Blender blend repair workflow' },
  { slug: 'design',       icon: <AutoFixHighOutlined />,            eyebrow: 'Design',                   title: 'Streamline 3D Design and Analysis',                        image: '/use-cases/optimization_workflow.png', imageAlt: 'Design optimization workflow' },
  { slug: 'multi',        icon: <HubOutlined />,                    eyebrow: 'Multi-physics',            title: 'Enable Integrated Multi-disciplinary Design',              image: '/use-cases/hpt_grid.png',            imageAlt: 'High-pressure turbine multi-component grid' },
  { slug: 'ai',           icon: <QueryStatsOutlined />,             eyebrow: 'AI & data generation',     title: 'Drive Data Generation for Artificial Intelligence',         image: '/use-cases/ai_workflow.png',         imageAlt: 'AI data generation workflow' },
  { slug: 'arbitrary',    icon: <TuneOutlined />,                   eyebrow: 'Design exploration',       title: 'Explore Free-Form Design',                                 image: '/use-cases/arbitrary_morph_box.png', imageAlt: 'Arbitrary shape morphing bounding box' },
  { slug: 'additive',     icon: <LayersOutlined />,                 eyebrow: 'Additive manufacturing',   title: 'Expedite Certification of Additively Manufactured Parts',  image: '/use-cases/additive_workflow.png',   imageAlt: 'Additive manufacturing certification workflow' },
  { slug: 'instrument',   icon: <SensorsOutlined />,                eyebrow: 'Test & instrumentation',   title: 'Improve Experimentation and Validation',                   image: '/use-cases/pbs4_gages.png',          imageAlt: 'Strain gauge placement on turbine rotor' },
  { slug: 'validation',   icon: <ScienceOutlined />,                eyebrow: 'Research',                 title: 'Validate Part-Specific Modeling',                          image: '/use-cases/exp-full-mode-192.png',   imageAlt: 'Experimental vs FEM mode shape comparison' },
  { slug: 'industry40',   icon: <PrecisionManufacturingOutlined />, eyebrow: 'Digital manufacturing',    title: 'Empower Industry 4.0',                                     image: '/use-cases/industry40_gpt3.png',     imageAlt: 'Industry 4.0 digital manufacturing workflow' },
  { slug: 'digital-twin', icon: <AccountTreeOutlined />,            eyebrow: 'Digital twin',             title: 'Deliver Instance-Level Digital Twins',                     image: '/use-cases/digital_twin_gpt2.png',   imageAlt: 'Instance-level digital twin lifecycle' },
];

const UseCasesSection: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box component="section" sx={{ bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider' }}>
      <Container maxWidth="xl" sx={{ py: { xs: 7, md: 10 } }}>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', md: 'flex-end' }} justifyContent="space-between" sx={{ mb: 5 }}>
          <Box>
            <Typography variant="overline" color="secondary.dark">Applications</Typography>
            <Typography variant="h3" sx={{ mt: 1, maxWidth: 560 }}>
              Where teams put FEMORPH to work.
            </Typography>
          </Box>
          <Button variant="outlined" onClick={() => navigate('/case-studies')}>
            View case studies
          </Button>
        </Stack>

        <Grid container spacing={2.5}>
          {useCases.map((uc) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={uc.slug}>
              <Card
                component={Link}
                to={`/use-cases/${uc.slug}`}
                aria-label={`Learn more about ${uc.title}`}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                  borderRadius: 1,
                  color: 'text.primary',
                  textDecoration: 'none',
                  transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 18px 36px rgba(23, 33, 38, 0.12)',
                    transform: 'translateY(-3px)',
                  },
                  '&:hover .uc-action': {
                    bgcolor: '#dbe9ec',
                    color: '#0b3848',
                  },
                  '&:hover .uc-action svg': {
                    transform: 'translateX(4px)',
                  },
                }}
              >
                {/* Dark icon header */}
                <Box
                  sx={{
                    px: 2.5,
                    pt: 2.5,
                    pb: 2,
                    bgcolor: '#172126',
                    color: 'white',
                    '& svg': { fontSize: 28 },
                  }}
                >
                  {uc.icon}
                </Box>

                <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                  <Typography variant="overline" color="secondary.dark">
                    {uc.eyebrow}
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 0.5, lineHeight: 1.3 }}>
                    {uc.title}
                  </Typography>
                </CardContent>

                {/* Image hidden — restore by removing display:'none' from sx below */}
                <CardMedia
                  component="img"
                  image={uc.image}
                  alt={uc.imageAlt}
                  sx={{
                    display: 'none',
                    height: 120,
                    objectFit: 'contain',
                    objectPosition: 'center',
                    bgcolor: '#eef3f1',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    p: 1,
                  }}
                />

                {/* Action footer */}
                <Stack
                  className="uc-action"
                  direction="row"
                  spacing={1}
                  sx={{
                    mt: 'auto',
                    px: 2.5,
                    py: 1.75,
                    alignItems: 'center',
                    color: 'primary.main',
                    fontWeight: 700,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    transition: 'background-color 160ms ease, color 160ms ease',
                  }}
                >
                  <Typography variant="button" component="span">Learn more</Typography>
                  <ArrowForward sx={{ color: 'inherit', fontSize: 18, transition: 'transform 160ms ease' }} />
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>

      </Container>
    </Box>
  );
};

export default UseCasesSection;
